import { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { FiRotateCcw } from "react-icons/fi";
import { openapi } from "../../openapi";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { useProjectId } from "../hooks/useProjectId";
import { makeSelectArticleValidationSettings } from "./articleValidationSettingsSelectors";

export default function ArticleValidationSettingsCard() {
  const projectNo = useProjectId();
  return <ProjectArticleValidationSettingsCard key={projectNo} projectNo={projectNo} />;
}

function ProjectArticleValidationSettingsCard({ projectNo }: { projectNo: number }) {
  const dispatch = useAppDispatch();
  const validProject = Number.isFinite(projectNo) && projectNo > 0;
  const { isLoading, isFetching, isError, refetch } =
    openapi.useGetMultiplacerProjectArticleValidationQuery(
      { projectNo },
      { skip: !validProject, refetchOnMountOrArgChange: true }
    );
  const selectSettings = useMemo(() => makeSelectArticleValidationSettings(projectNo), [projectNo]);
  const { exclusions, canEdit } = useAppSelector(selectSettings);
  const [setIgnored, { isLoading: isSaving }] =
    openapi.usePostMultiplacerProjectArticleValidationIgnoredMutation();
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [removed, setRemoved] = useState<string | null>(null);

  async function remove() {
    if (!selected) return;
    setError(null);
    try {
      await setIgnored({ body: { projectNo, compCode: selected, ignored: false } }).unwrap();
      setRemoved(selected);
      setSelected(null);
      dispatch(openapi.util.invalidateTags(["projects"]));
      await refetch();
    } catch {
      setError("Kunne ikke fjerne unntaket. Prøv igjen.");
    }
  }

  return (
    <Card withBorder radius="lg" p="lg">
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={4}>Artikler unntatt fra validering</Title>
          {!isLoading && !isError && validProject && (
            <Badge variant="light">{exclusions.length}</Badge>
          )}
        </Group>
        <Text size="sm" c="dimmed">
          Disse komponentkodene er unntatt fra artikkelvalideringen i alle rom i prosjektet, på
          tvers av lister og ansvar. Listen viser prosjektets innstillinger, også endringer som ennå
          ikke er brukt i en validering.
        </Text>
        <Text size="sm" c="dimmed">
          Når du fjerner et unntak, tas artikkelen med igjen ved neste synkronisering som kjører
          validering, eller når du velger «Revalider rom». Ingen artikler slettes fra modellen eller
          dRofus.
        </Text>
        {removed && (
          <Alert color="blue" role="status">
            Unntaket for {removed} er fjernet. Lagrede valideringsresultater oppdateres først ved
            neste validering.
          </Alert>
        )}
        {!validProject ? (
          <Alert color="red">Velg et gyldig prosjekt.</Alert>
        ) : isLoading ? (
          <Group>
            <Loader size="sm" />
            <Text size="sm">Henter unntak…</Text>
          </Group>
        ) : isError ? (
          <Alert color="red">
            Kunne ikke hente unntakslisten.{" "}
            <Button variant="subtle" size="xs" onClick={() => void refetch()}>
              Prøv igjen
            </Button>
          </Alert>
        ) : (
          <>
            {!canEdit && (
              <Text size="sm" c="dimmed">
                Kun interne brukere og prosjektadministratorer kan fjerne unntak.
              </Text>
            )}
            {exclusions.length === 0 ? (
              <Text size="sm">Ingen artikler er unntatt fra validering.</Text>
            ) : (
              <ScrollArea.Autosize mah={360} type="auto" offsetScrollbars>
                <Stack
                  gap="xs"
                  component="ul"
                  aria-label="Unntatte komponentkoder"
                  style={{ listStyle: "none", margin: 0, padding: 0 }}
                >
                  {exclusions.map(({ compCode, description, addedBy, addedAt }) => (
                    <Group component="li" key={compCode} justify="space-between" wrap="nowrap">
                      <Stack gap={3} style={{ overflowWrap: "anywhere", minWidth: 0 }}>
                        <Text size="sm" fw={600}>
                          {compCode}
                        </Text>
                        <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                          {description}
                        </Text>
                        <Text size="xs" c="dimmed">
                          Lagt til av {addedBy.displayName} ·{" "}
                          {new Date(addedAt).toLocaleString("nb-NO")}
                        </Text>
                      </Stack>
                      <Button
                        variant="subtle"
                        size="xs"
                        leftSection={<FiRotateCcw size={14} />}
                        aria-label={`Fjern unntak for ${compCode}`}
                        disabled={!canEdit || isSaving || isFetching}
                        style={{ flexShrink: 0 }}
                        onClick={() => {
                          setError(null);
                          setSelected(compCode);
                        }}
                      >
                        Fjern unntak
                      </Button>
                    </Group>
                  ))}
                </Stack>
              </ScrollArea.Autosize>
            )}
          </>
        )}
      </Stack>
      <Modal
        opened={selected != null}
        onClose={() => {
          if (!isSaving) setSelected(null);
        }}
        title="Fjerne unntaket?"
        centered
      >
        <Stack>
          <Text fw={600}>{selected}</Text>
          <Text size="sm">
            Artikkelkoden tas med i artikkelvalideringen igjen i alle rom i dette prosjektet.
          </Text>
          <Alert color="blue">
            Endringen gjelder først ved neste synkronisering som kjører validering, eller ved
            manuell revalidering av rommet. Lagrede resultater endres ikke nå.
          </Alert>
          {error && (
            <Alert color="red" role="alert">
              {error}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button variant="neutral" disabled={isSaving} onClick={() => setSelected(null)}>
              Avbryt
            </Button>
            <Button loading={isSaving} onClick={() => void remove()}>
              Fjern unntak
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}
